import moment from 'moment';
import { AMENITY_PARKING } from '../../../helper/types';
import { MAX_ADVANCE_BOOKING_DAYS } from '../../properties/Unit&Amenities/Amenities/V2/addAmenityScheduleHelpersV2';

export const BOOKING_FORM_NO_TIME = -1;

export const BOOKING_TYPE_RESIDENT = 0;
export const BOOKING_TYPE_BLOCK = 1;
export const AVAILABILITY_QUERY_TYPE_BLOCK = 'block';
export const BLOCK_TIME_STEP_MINUTES = 30;

export function isUnsetTime(v) {
  return v == null || v === '' || v === BOOKING_FORM_NO_TIME || v === String(BOOKING_FORM_NO_TIME);
}

export function formatTimeForDisplay(isoString) {
  if (!isoString || isUnsetTime(isoString)) return '';
  const m = moment.parseZone(isoString);
  return m.isValid() ? m.format('h:mm A') : '';
}

export function isFieldInTransition(value, prevValue) {
  const wasSet = !isUnsetTime(prevValue) && prevValue != null;
  const nowUnset = isUnsetTime(value);
  return wasSet && nowUnset;
}

export function bookingFieldDateKey(value) {
  if (value == null || value === '') return '';
  const m = moment(value);
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
}

export function getCheckoutDatesFromEndOptions(endTimeOptions) {
  if (!Array.isArray(endTimeOptions) || endTimeOptions.length === 0) {
    return new Set();
  }
  const dates = new Set();
  for (const iso of endTimeOptions) {
    const m = moment.parseZone(iso);
    if (m.isValid()) {
      dates.add(m.format('YYYY-MM-DD'));
    }
  }
  return dates;
}

export function resolveSlotStepMinutes(amenityRow) {
  const n = Number(amenityRow?.slot_duration);
  if (!Number.isFinite(n) || n <= 0) return 30;
  return n === 60 ? 60 : 30;
}

export function resolvePolicyMinMinutes(amenityRow) {
  if (!amenityRow) return 0;
  const minTime = Number(amenityRow.min_time);
  if (!Number.isFinite(minTime) || minTime <= 0) return 0;
  if (amenityRow.allow_multi_day_bookings) {
    const unit = Number(amenityRow.booking_limit_unit);
    if (unit === 1) {
      return minTime * 24 * 60;
    }
    return minTime * 60;
  }
  return minTime * 60;
}

export function resolveEffectiveMinBookingMinutes(amenityRow) {
  const slot = resolveSlotStepMinutes(amenityRow);
  const policy = resolvePolicyMinMinutes(amenityRow);
  if (policy > 0) {
    return Math.max(policy, slot);
  }
  return slot;
}

export function resolveAdvanceBookingLimitDays(amenityRow) {
  const raw = amenityRow?.advance_booking_limit_days;
  const n =
    raw != null && raw !== '' && !Number.isNaN(Number(raw)) ? Number(raw) : 30;
  return Math.min(MAX_ADVANCE_BOOKING_DAYS, Math.max(1, n));
}

/** API schedule: day_of_week 1 = Monday … 7 = Sunday. */
export function momentDayToApiDayOfWeek(momentDay) {
  return momentDay === 0 ? 7 : momentDay;
}

/**
 * @returns {Set<number>|null} Open API weekdays, or null if no schedules to apply.
 */
export function getOpenScheduleDaysOfWeek(amenities_schedules) {
  if (!Array.isArray(amenities_schedules) || amenities_schedules.length === 0) {
    return null;
  }
  const open = new Set();
  for (const row of amenities_schedules) {
    if (row?.is_closed === false && row.day_of_week >= 1 && row.day_of_week <= 7) {
      open.add(row.day_of_week);
    }
  }
  return open;
}

export function isDateOnClosedScheduleDay(dateMoment, amenities_schedules) {
  const open = getOpenScheduleDaysOfWeek(amenities_schedules);
  if (open === null) return false;
  const apiDow = momentDayToApiDayOfWeek(dateMoment.day());
  return !open.has(apiDow);
}

export function isStartDateWithinAdvanceLimit(startDate, limitDays) {
  if (startDate == null || startDate === '') return true;
  const start = moment.isMoment(startDate) ? startDate.clone() : moment(startDate);
  if (!start.isValid()) return true;
  const today = moment().startOf('day');
  const maxBookable = today.clone().add(limitDays, 'days');
  const day = start.clone().startOf('day');
  return day.isSameOrAfter(today, 'day') && day.isSameOrBefore(maxBookable, 'day');
}

export function getAdvanceBookingStartDateYupConfig(t, amenityRow) {
  const limitDays = resolveAdvanceBookingLimitDays(amenityRow);
  return {
    limitDays,
    message: t('booking_start_date_advance_limit', {
      defaultValue: 'Start date must be within {{days}} days from today.',
      days: limitDays
    }),
    test: (value) => isStartDateWithinAdvanceLimit(value, limitDays)
  };
}

/** All valid checkout instants from start through max_end, stepped by slot duration and min length. */
export function buildEndTimeIsoOptions(startIso, maxEndIso, stepMinutes, minBookingMinutes) {
  if (!startIso || !maxEndIso || !stepMinutes || !minBookingMinutes) return [];
  const start = moment.parseZone(startIso);
  const maxEnd = moment.parseZone(maxEndIso);
  if (!start.isValid() || !maxEnd.isValid()) return [];
  const out = [];
  let t = start.clone().add(stepMinutes, 'minutes');
  while (t.diff(start, 'minutes') < minBookingMinutes) {
    t.add(stepMinutes, 'minutes');
  }
  while (t.isSameOrBefore(maxEnd)) {
    out.push(t.format());
    t.add(stepMinutes, 'minutes');
  }
  return out;
}

/** Step ISO instants from `from` through `to` inclusive, on a fixed step grid anchored at `gridAnchor`. */
function buildBlockSteppedOptions(gridAnchor, fromMoment, toMoment, stepMinutes) {
  if (!gridAnchor?.isValid() || !fromMoment?.isValid() || !toMoment?.isValid() || !stepMinutes) {
    return [];
  }
  if (fromMoment.isAfter(toMoment)) return [];

  const out = [];
  let t = gridAnchor.clone();
  while (t.isBefore(fromMoment)) {
    t.add(stepMinutes, 'minutes');
  }
  while (t.isSameOrBefore(toMoment)) {
    out.push(t.format());
    t.add(stepMinutes, 'minutes');
  }
  return out;
}

/**
 * Block start options: 30-min steps within each interval on the selected day.
 * Last start = max_end - step so a minimum block always fits.
 */
export function buildBlockStartTimeOptions(
  intervals,
  selectedDateYmd,
  stepMinutes = BLOCK_TIME_STEP_MINUTES
) {
  if (!Array.isArray(intervals) || !selectedDateYmd || !stepMinutes) return [];

  const seen = new Set();
  const out = [];

  for (const row of intervals) {
    if (!row?.start_time || !row?.max_end_time) continue;
    const intervalStart = moment.parseZone(row.start_time);
    const intervalMaxEnd = moment.parseZone(row.max_end_time);
    if (!intervalStart.isValid() || !intervalMaxEnd.isValid()) continue;

    const lastValidStart = intervalMaxEnd.clone().subtract(stepMinutes, 'minutes');
    if (lastValidStart.isBefore(intervalStart)) continue;

    const offset = intervalStart.format('Z');
    const dayStart = moment.parseZone(`${selectedDateYmd}T00:00:00${offset}`);
    const dayLastStart = dayStart.clone().add(23, 'hours').add(30, 'minutes');

    if (intervalMaxEnd.isBefore(dayStart) || intervalStart.isAfter(dayLastStart)) continue;

    const effectiveFrom = intervalStart.isAfter(dayStart) ? intervalStart : dayStart;
    const effectiveTo = lastValidStart.isBefore(dayLastStart) ? lastValidStart : dayLastStart;
    if (effectiveFrom.isAfter(effectiveTo)) continue;

    for (const iso of buildBlockSteppedOptions(
      intervalStart,
      effectiveFrom,
      effectiveTo,
      stepMinutes
    )) {
      if (moment.parseZone(iso).format('YYYY-MM-DD') === selectedDateYmd && !seen.has(iso)) {
        seen.add(iso);
        out.push(iso);
      }
    }
  }

  return out.sort((a, b) => moment.parseZone(a).valueOf() - moment.parseZone(b).valueOf());
}

/** Resolve the interval for a chosen block start; prefers widest max_end when overlapping. */
export function findBlockIntervalForStart(
  intervals,
  startIso,
  stepMinutes = BLOCK_TIME_STEP_MINUTES
) {
  if (!Array.isArray(intervals) || !startIso) return null;

  const start = moment.parseZone(startIso);
  if (!start.isValid()) return null;

  let best = null;
  let bestMaxEnd = null;

  for (const row of intervals) {
    if (!row?.start_time || !row?.max_end_time) continue;
    const intervalStart = moment.parseZone(row.start_time);
    const intervalMaxEnd = moment.parseZone(row.max_end_time);
    if (!intervalStart.isValid() || !intervalMaxEnd.isValid()) continue;

    const lastValidStart = intervalMaxEnd.clone().subtract(stepMinutes, 'minutes');
    if (start.isBefore(intervalStart) || start.isAfter(lastValidStart)) continue;

    if (!best || intervalMaxEnd.isAfter(bestMaxEnd)) {
      best = row;
      bestMaxEnd = intervalMaxEnd;
    }
  }

  return best;
}

/** Block end options: reuse booking stepper with min duration = step (no amenity min_time). */
export function buildBlockEndTimeOptions(startIso, maxEndIso, stepMinutes = BLOCK_TIME_STEP_MINUTES) {
  return buildEndTimeIsoOptions(startIso, maxEndIso, stepMinutes, stepMinutes);
}

export function buildBlockCreatePayload(values) {
  return {
    type: BOOKING_TYPE_BLOCK,
    building_id: values.building.value,
    amenity_id: parseInt(values.amenity_id, 10),
    title: values.reason.trim(),
    start_date: values.startTime,
    end_date: values.endTime,
    data: {}
  };
}

export function buildBlockEditPayload(values, booking) {
  return {
    amenity_id: parseInt(booking.amenity_id, 10),
    title: values.reason.trim(),
    start_date: values.startTime,
    end_date: values.endTime,
    data: booking.data ?? {}
  };
}

export function buildBlockEditFormDefaults(booking) {
  if (!booking?.start_date) return null;

  const buildingLabel =
    booking.building ||
    booking.building_name ||
    booking.tenant?.building ||
    String(booking.building_id ?? '');

  const startMom = moment.parseZone(booking.start_date);
  const endMom = booking.end_date ? moment.parseZone(booking.end_date) : null;

  return {
    building: {
      value: booking.building_id,
      label: buildingLabel
    },
    amenity_id: booking.amenity_id,
    startDate: moment.parseZone(booking.start_date),
    endDate: booking.end_date ? moment.parseZone(booking.end_date) : undefined,
    startTime: startMom.isValid() ? startMom.format() : BOOKING_FORM_NO_TIME,
    endTime: endMom?.isValid() ? endMom.format() : BOOKING_FORM_NO_TIME,
    reason: booking.title ?? ''
  };
}

export function amenityRequiresReservationForm(amenity) {
  return Boolean(amenity?.require_reservation_form && amenity?.waiver_form?.id);
}

export function amenityFormRequiredButMissing(amenity) {
  return Boolean(amenity?.require_reservation_form && !amenity?.waiver_form?.id);
}

export function buildBookingCreatePayload(values, amenityRow, amenityDetail) {
  const description = amenityRow ? amenityRow.description : '';
  const body = {
    type: BOOKING_TYPE_RESIDENT,
    user_id: values.user.value,
    building_id: values.building.value,
    amenity_id: parseInt(values.amenity_id, 10),
    title: description,
    start_date: values.startTime,
    end_date: values.endTime,
    data: {}
  };

  if (amenityRow?.type_id === AMENITY_PARKING) {
    body.data = {
      make: values?.make,
      model: values?.model,
      color: values?.color,
      plate: values?.plate_number
    };
  }

  if (amenityRequiresReservationForm(amenityDetail)) {
    body.reservation_form_id = amenityDetail.waiver_form.id;
  }

  return body;
}

export function buildBookingEditPayload(values, booking, isParking, amenityDetail) {
  let data = booking.data ?? {};
  if (isParking) {
    data = {
      make: values?.make,
      model: values?.model,
      color: values?.color,
      plate: values?.plate_number
    };
  }

  const body = {
    amenity_id: parseInt(booking.amenity_id, 10),
    start_date: values.startTime,
    end_date: values.endTime,
    data
  };

  if (parseInt(booking.type, 10) === BOOKING_TYPE_BLOCK) {
    body.title = booking.title;
  }

  if (amenityRequiresReservationForm(amenityDetail)) {
    body.reservation_form_id = amenityDetail.waiver_form.id;
  }

  return body;
}

export function buildBookingEditFormDefaults(booking) {
  if (!booking?.start_date) return null;
  const data = booking.data || {};
  const buildingLabel =
    booking.building ||
    booking.building_name ||
    booking.tenant?.building ||
    String(booking.building_id ?? '');

  const startMom = moment.parseZone(booking.start_date);
  const endMom = booking.end_date ? moment.parseZone(booking.end_date) : null;

  return {
    building: {
      value: booking.building_id,
      label: buildingLabel
    },
    user: {
      value: booking.user_id ?? booking.tenant?.user_id ?? booking.tenant?.id,
      label: booking.tenant?.name ?? '',
      unit_num: booking.tenant?.unit_num,
      unit_number: booking.tenant?.unit_num
    },
    amenity_id: booking.amenity_id,
    startDate: moment.parseZone(booking.start_date),
    endDate: booking.end_date ? moment.parseZone(booking.end_date) : undefined,
    startTime: startMom.isValid() ? startMom.format() : BOOKING_FORM_NO_TIME,
    endTime: endMom?.isValid() ? endMom.format() : BOOKING_FORM_NO_TIME,
    make: data.make ?? '',
    model: data.model ?? '',
    color: data.color ?? '',
    plate_number: data.plate ?? data.plate_number ?? ''
  };
}

export function buildBlockValidationSchema({
  yup,
  t,
  amenity,
  availabilitySlots,
  verifyTimeslot
}) {
  const advanceStartDate = getAdvanceBookingStartDateYupConfig(t, amenity);

  return yup.object().shape({
    building: yup.object().required(t('Building is required')),
    amenity_id: yup
      .string()
      .typeError(t('Required!'))
      .required(t('Required!'))
      .test('required', t('Required!'), (value) => {
        if (!value || parseInt(value, 10) <= 0) return false;
        return true;
      }),
    startDate: yup
      .string()
      .required(t('Start date is required'))
      .typeError(t('Start date is required'))
      .test(
        'within-advance-booking-limit',
        advanceStartDate.message,
        (value) => advanceStartDate.test(value)
      )
      .test('no-blockable-time', function (value) {
        if (!verifyTimeslot) return true;
        if (availabilitySlots != null && availabilitySlots.length === 0) {
          const dateStr = value ? moment(value).format('MMMM D, YYYY') : 'this date';
          return this.createError({
            message: t('no_blockable_time_available', {
              defaultValue: 'No blockable time is available for {{date}}. Please choose a different date.',
              date: dateStr
            })
          });
        }
        return true;
      }),
    endDate: yup
      .string()
      .required(t('End date is required'))
      .typeError(t('End date is required'))
      .test(
        'end-on-or-after-start',
        t('End date must be on or after start date'),
        function (value) {
          const { startDate: sd } = this.parent;
          if (!value || !sd) return true;
          const endMoment = moment.isMoment(value) ? value : moment(value);
          const startMoment = moment.isMoment(sd) ? sd : moment(sd);
          return !endMoment.isBefore(startMoment, 'day');
        }
      )
      .test(
        'end-within-interval-max-day',
        t('block_end_after_max', {
          defaultValue: 'End must be within the allowed range for this start time.'
        }),
        function (value) {
          const { startTime: st } = this.parent;
          if (!value || isUnsetTime(st) || typeof st !== 'string') return true;
          const row = findBlockIntervalForStart(availabilitySlots, st);
          if (!row?.max_end_time) return true;
          const maxEnd = moment.parseZone(row.max_end_time);
          if (!maxEnd.isValid()) return true;
          const endMoment = moment.isMoment(value) ? value : moment(value);
          return !endMoment.isAfter(maxEnd, 'day');
        }
      ),
    startTime: yup
      .mixed()
      .test('isValidStartTime', t('Start time is required'), function (value) {
        if (isUnsetTime(value)) return false;
        return typeof value === 'string' && moment.parseZone(value).isValid();
      })
      .test('start-within-blockable-interval', function (value) {
        if (isUnsetTime(value) || typeof value !== 'string') return true;
        if (!verifyTimeslot || !availabilitySlots?.length) return true;

        const ymd = bookingFieldDateKey(this.parent.startDate);
        if (!ymd) return true;

        const options = buildBlockStartTimeOptions(availabilitySlots, ymd);
        if (options.includes(value)) return true;
        if (findBlockIntervalForStart(availabilitySlots, value)) return true;

        return this.createError({
          message: t('block_start_outside_interval', {
            defaultValue: 'Start time is outside the blockable range for this date.'
          })
        });
      })
      .test(
        'startTimeBeforeEndTime',
        function (value) {
          if (isUnsetTime(value)) return true;
          if (isUnsetTime(this.parent.endTime)) return true;

          const startMoment = moment.parseZone(value);
          const endMoment = moment.parseZone(this.parent.endTime);
          if (!startMoment.isValid() || !endMoment.isValid()) return true;

          if (!endMoment.isAfter(startMoment)) {
            return this.createError({
              message: t('start_time_after_end_time_error', {
                defaultValue: 'Start time ({{startTime}}) must be before end time ({{endTime}})',
                startTime: formatTimeForDisplay(value),
                endTime: formatTimeForDisplay(this.parent.endTime)
              })
            });
          }
          return true;
        }
      ),
    endTime: yup
      .mixed()
      .test('isValidEndTime', t('End time is required'), function (value) {
        if (isUnsetTime(value)) return false;
        return typeof value === 'string' && moment.parseZone(value).isValid();
      })
      .test(
        'endTimeAfterStartTime',
        function (value) {
          if (isUnsetTime(value)) return true;
          if (isUnsetTime(this.parent.startTime)) return true;

          const startMoment = moment.parseZone(this.parent.startTime);
          const endMoment = moment.parseZone(value);
          if (!startMoment.isValid() || !endMoment.isValid()) return true;

          if (!endMoment.isAfter(startMoment)) {
            return this.createError({
              message: t('end_time_before_start_time_error', {
                defaultValue: 'End time ({{endTime}}) must be after start time ({{startTime}})',
                startTime: formatTimeForDisplay(this.parent.startTime),
                endTime: formatTimeForDisplay(value)
              })
            });
          }
          return true;
        }
      )
      .test(
        'end-within-max-end-time',
        function (value) {
          if (isUnsetTime(value)) return true;
          const startTime = this.parent.startTime;
          if (isUnsetTime(startTime) || typeof startTime !== 'string') return true;

          const row = findBlockIntervalForStart(availabilitySlots, startTime);
          if (!row?.max_end_time) return true;

          const maxEndMoment = moment.parseZone(row.max_end_time);
          const endMoment = moment.parseZone(value);
          if (!maxEndMoment.isValid() || !endMoment.isValid()) return true;

          if (!endMoment.isSameOrBefore(maxEndMoment)) {
            return this.createError({
              message: t('block_end_after_max', {
                defaultValue: 'End must be by {{maxTime}} for this start time.',
                maxTime: formatTimeForDisplay(row.max_end_time)
              })
            });
          }
          return true;
        }
      ),
    reason: yup.string().trim().required(t('Required!'))
  });
}

export function buildBookingValidationSchema({
  yup,
  t,
  amenity,
  availabilitySlots,
  verifyTimeslot,
  selectedAmenityId,
  requiresReservationForm = false
}) {
  const allowMultiDay = !!amenity?.allow_multi_day_bookings;
  const minBookingMins = resolveEffectiveMinBookingMinutes(amenity);
  const advanceStartDate = getAdvanceBookingStartDateYupConfig(t, amenity);

  return yup.object().shape({
    building: yup.object().required(t('Building is required')),
    amenity_id: yup
      .string()
      .typeError(t('Required!'))
      .required(t('Required!'))
      .test('required', t('Required!'), (value) => {
        if (!value || parseInt(value, 10) <= 0) return false;
        return true;
      }),
    user: yup.object().test('required', t('Required!'), (value) => {
      if (!value || value.value === 0) return false;
      return true;
    }),
    startDate: yup
      .string()
      .required(t('Start date is required'))
      .typeError(t('Start date is required'))
      .test(
        'within-advance-booking-limit',
        advanceStartDate.message,
        (value) => advanceStartDate.test(value)
      )
      .test(
        'no-time-slots',
        function (value) {
          if (!verifyTimeslot) return true;
          if (availabilitySlots != null && availabilitySlots.length === 0) {
            const dateStr = value ? moment(value).format('MMMM D, YYYY') : 'this date';
            return this.createError({
              message: t('no_time_slots_available', {
                defaultValue: 'No time slots are available for {{date}}. Please choose a different date.',
                date: dateStr
              })
            });
          }
          return true;
        }
      ),
    endDate: allowMultiDay
      ? yup
          .string()
          .required(t('End date is required'))
          .typeError(t('End date is required'))
          .test(
            'end-on-or-after-start',
            t('End date must be on or after start date'),
            function (value) {
              const { startDate: sd } = this.parent;
              if (!value || !sd) return true;
              const endMoment = moment.isMoment(value) ? value : moment(value);
              const startMoment = moment.isMoment(sd) ? sd : moment(sd);
              return !endMoment.isBefore(startMoment, 'day');
            }
          )
          .test(
            'end-within-slot-max-day',
            t('booking_end_after_max', {
              defaultValue: 'Check-out must be within the allowed range for this check-in time.'
            }),
            function (value) {
              const { startTime: st } = this.parent;
              if (!value || isUnsetTime(st) || typeof st !== 'string') return true;
              const row = availabilitySlots.find((s) => s.start_time === st);
              if (!row?.max_end_time) return true;
              const maxEnd = moment.parseZone(row.max_end_time);
              if (!maxEnd.isValid()) return true;
              const endMoment = moment.isMoment(value) ? value : moment(value);
              return !endMoment.isAfter(maxEnd, 'day');
            }
          )
      : yup.mixed().notRequired().nullable(),
    startTime: yup
      .mixed()
      .test('isValidStartTime', t('Start time is required'), function (value) {
        if (isUnsetTime(value)) return false;
        return typeof value === 'string' && moment.parseZone(value).isValid();
      })
      .test(
        'startTimeBeforeEndTime',
        function (value) {
          if (isUnsetTime(value)) return true;
          if (isUnsetTime(this.parent.endTime)) return true;
          
          const startMoment = moment.parseZone(value);
          const endMoment = moment.parseZone(this.parent.endTime);
          if (!startMoment.isValid() || !endMoment.isValid()) return true;
          
          const isValid = endMoment.isAfter(startMoment);
          if (!isValid) {
            // Use formatted times in error message
            const startTime = formatTimeForDisplay(value);
            const endTime = formatTimeForDisplay(this.parent.endTime);
            return this.createError({
              message: t('start_time_after_end_time_error', {
                defaultValue: 'Start time ({{startTime}}) must be before end time ({{endTime}})',
                startTime,
                endTime
              })
            });
          }
          return true;
        }
      ),
    endTime: yup
      .mixed()
      .test('isValidEndTime', t('End time is required'), function (value) {
        if (isUnsetTime(value)) return false;
        return typeof value === 'string' && moment.parseZone(value).isValid();
      })
      .test(
        'endTimeAfterStartTime',
        function (value) {
          if (isUnsetTime(value)) return true;
          if (isUnsetTime(this.parent.startTime)) return true;
          
          const startMoment = moment.parseZone(this.parent.startTime);
          const endMoment = moment.parseZone(value);
          if (!startMoment.isValid() || !endMoment.isValid()) return true;
          
          const isValid = endMoment.isAfter(startMoment);
          if (!isValid) {
            // Use formatted times in error message
            const startTime = formatTimeForDisplay(this.parent.startTime);
            const endTime = formatTimeForDisplay(value);
            return this.createError({
              message: t('end_time_before_start_time_error', {
                defaultValue: 'End time ({{endTime}}) must be after start time ({{startTime}})',
                startTime,
                endTime
              })
            });
          }
          return true;
        }
      )
      .test(
        'min-booking-duration',
        function (value) {
          if (isUnsetTime(value)) return true;
          if (isUnsetTime(this.parent.startTime)) return true;
          
          const startMoment = moment.parseZone(this.parent.startTime);
          const endMoment = moment.parseZone(value);
          if (!startMoment.isValid() || !endMoment.isValid()) return true;
          
          const durationMinutes = endMoment.diff(startMoment, 'minutes');
          const isValid = durationMinutes >= minBookingMins;
          if (!isValid) {
            const hours = Math.floor(minBookingMins / 60);
            const minutes = minBookingMins % 60;
            const durationText = hours > 0 
              ? (minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`)
              : `${minutes}m`;
            
            return this.createError({
              message: t('booking_min_duration_error', {
                defaultValue: 'Booking must be at least {{duration}} ({{minutes}} minutes).',
                duration: durationText,
                minutes: minBookingMins
              })
            });
          }
          return true;
        }
      )
      .test(
        'end-within-max-end-time',
        function (value) {
          if (isUnsetTime(value)) return true;
          const startTime = this.parent.startTime;
          if (isUnsetTime(startTime) || typeof startTime !== 'string') return true;
          
          const row = availabilitySlots.find((s) => s.start_time === startTime);
          if (!row?.max_end_time) return true;
          
          const maxEndMoment = moment.parseZone(row.max_end_time);
          const endMoment = moment.parseZone(value);
          if (!maxEndMoment.isValid() || !endMoment.isValid()) return true;
          
          const isValid = endMoment.isSameOrBefore(maxEndMoment);
          if (!isValid) {
            const maxEndTime = formatTimeForDisplay(row.max_end_time);
            return this.createError({
              message: t('booking_end_after_max', {
                defaultValue: 'Check-out must be by {{maxTime}} for this check-in time.',
                maxTime: maxEndTime
              })
            });
          }
          return true;
        }
      ),
    make: yup.string().test('is-parking', t('Required'), (value) => {
      if (selectedAmenityId === AMENITY_PARKING && !value) return false;
      if (selectedAmenityId === AMENITY_PARKING && value && value.length === 0) return false;
      return true;
    }),
    model: yup.string().test('is-parking', t('Required'), (value) => {
      if (selectedAmenityId === AMENITY_PARKING && !value) return false;
      if (selectedAmenityId === AMENITY_PARKING && value && value.length === 0) return false;
      return true;
    }),
    color: yup.string().test('is-parking', t('Required'), (value) => {
      if (selectedAmenityId === AMENITY_PARKING && !value) return false;
      if (selectedAmenityId === AMENITY_PARKING && value && value.length === 0) return false;
      return true;
    }),
    plate_number: yup.string().test('is-parking', t('Required'), (value) => {
      if (selectedAmenityId === AMENITY_PARKING && !value) return false;
      if (selectedAmenityId === AMENITY_PARKING && value && value.length === 0) return false;
      return true;
    }),
    reservationFormConsent: requiresReservationForm
      ? yup
          .boolean()
          .oneOf(
            [true],
            t('book_amenity_form_consent_required', {
              defaultValue: 'Consent is required to continue.'
            })
          )
      : yup.boolean().notRequired()
  });
}
